import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {useInterviewStore} from "@/stores/interview-store";
import {surveyApi} from "@/container";
import {SurveyAnswerInstance} from "@/core/interfaces/survey/Survey";
import {InterviewInstance} from "@/interview/interfaces/interview";
import {storeToRefs} from "pinia";

export type PrefillStatus = 'idle' | 'running' | 'completed' | 'error';

interface PrefillEventData {
    success: boolean;
    instance: {
        id: number;
        ended_at?: string;
        survey_answer_instance_id: number;
    };
}

/**
 * Composable for managing the interview survey prefill state.
 *
 * States:
 * - idle: Initial state, waiting to determine next action
 * - running: Prefill job is in progress (either started by us or already running)
 * - completed: Survey instance is loaded and ready
 * - error: Prefill job failed
 */
const useInterviewPrefill = () => {
    const interviewStore = useInterviewStore();
    const {interviewInstance, interview, consumerKey} = storeToRefs(interviewStore);

    const status = ref<PrefillStatus>('idle');
    const surveyInstance = ref<SurveyAnswerInstance>();
    const error = ref(false);

    let removeSocketListener: (() => void) | null = null;

    /**
     * Determine the initial prefill status based on the current interviewInstance state.
     */
    const determineInitialStatus = (instance: InterviewInstance | null): PrefillStatus => {
        if (!instance) {
            return 'idle';
        }

        if (!instance?.ended_at && instance.prefill_job_started_at && !instance.prefill_job_ended_at) {
            return 'running';
        }

        if (instance.survey_answer_instance_id) {
            return 'completed';
        }

        return 'idle';
    };

    /**
     * Check if we can start the prefill job.
     */
    const canStartPrefill = (): boolean => {
        return !!(
            interviewInstance.value?.id &&
            interview.value &&
            consumerKey.value &&
            status.value === 'idle'
        );
    };

    /**
     * Load the survey answer instance by ID.
     */
    const loadSurveyInstance = async (id: number): Promise<void> => {
        surveyInstance.value = await surveyApi().builtAnsweredInstance(id);
    };

    /**
     * Handle the prefill completion event from the socket.
     */
    const handlePrefillComplete = async (data: PrefillEventData): Promise<void> => {
        if (data.success) {
            if (interviewInstance.value) {
                interviewInstance.value.ended_at = data.instance.ended_at;
                interviewInstance.value.survey_answer_instance_id = data.instance.survey_answer_instance_id;

                interviewStore.updatePatientStore(interviewInstance.value);
            }
            await loadSurveyInstance(data.instance.survey_answer_instance_id);
            status.value = 'completed';
        } else {
            status.value = 'error';
            error.value = true;
        }
    };

    /**
     * Set up the socket listener for prefill completion events.
     */
    const setupSocketListener = (): void => {
        if (removeSocketListener) {
            return; // Already listening
        }

        removeSocketListener = window.ipcRenderer.socketListenEvent(
            'interview-instance.survey.prefilled',
            (event, {data}: { data: PrefillEventData }) => {
                if (data.instance?.id === interviewInstance.value?.id) {
                    handlePrefillComplete(data);
                }
            }
        );
    };

    /**
     * Clean up the socket listener.
     */
    const cleanupSocketListener = (): void => {
        if (removeSocketListener) {
            removeSocketListener();
            removeSocketListener = null;
        }
    };

    /**
     * Start the prefill process.
     */
    const startPrefill = async (): Promise<void> => {
        if (!canStartPrefill()) {
            return;
        }

        status.value = 'running';
        setupSocketListener();
        await interviewStore.runPrefillSurveyJob();
    };

    /**
     * Initialize the prefill state based on current interviewInstance.
     */
    const initialize = async (): Promise<void> => {
        const initialStatus = determineInitialStatus(interviewInstance.value);
        status.value = initialStatus;

        if (initialStatus === 'completed' && interviewInstance.value?.survey_answer_instance_id) {
            await loadSurveyInstance(interviewInstance.value.survey_answer_instance_id);
        } else if (initialStatus === 'running') {
            setupSocketListener();
        } else if (initialStatus === 'idle' && canStartPrefill()) {
            await startPrefill();
        }
    };

    /**
     * Ready when we have interviewInstance AND either:
     * 1. Survey instance is loaded
     * 2. Prefill failed (show error state)
     * 3. Interview doesn't require a survey (survey is not relevant)
     */
    const isReady = computed((): boolean => {
        return !!(
            interviewInstance.value && (
                surveyInstance.value ||
                status.value === 'error' ||
                (!interview.value?.is_survey_relevant && status.value === 'completed')
            )
        );
    });

    const isPrefillComplete = computed((): boolean => {
        return status.value === 'completed' || status.value === 'error';
    });

    const isWaitingForPrefill = computed((): boolean => {
        return status.value === 'running' || status.value === 'idle';
    });

    watch(
        () => interviewInstance.value?.id,
        (newId, oldId) => {
            if (newId && newId !== oldId && status.value === 'idle') {
                initialize();
            }
        }
    );


    onMounted(() => {
        if (interviewInstance.value?.id) {
            initialize();
        }
    });

    onUnmounted(() => {
        cleanupSocketListener();
    });

    return {
        status,
        surveyInstance,
        error,

        isReady,
        isPrefillComplete,
        isWaitingForPrefill,

        startPrefill
    };
};

export default useInterviewPrefill;
